// Sentiment Analysis for Trading
export class SentimentAnalyzer {
  constructor() {
    this.newsSources = [
      'coindesk.com',
      'cointelegraph.com',
      'decrypt.co',
      'theblock.co',
      'cryptonews.com'
    ];
    
    this.socialSources = [
      'twitter.com',
      'reddit.com/r/cryptocurrency',
      'reddit.com/r/bitcoin',
      'reddit.com/r/ethereum'
    ];

    this.sentimentKeywords = {
      positive: [
        'bullish', 'moon', 'pump', 'surge', 'rally', 'breakout', 'strong', 'growth',
        'adoption', 'institutional', 'hodl', 'diamond hands', 'to the moon',
        'breakthrough', 'milestone', 'partnership', 'upgrade', 'launch',
        'bull market', 'green', 'gains', 'profit', 'success', 'win'
      ],
      negative: [
        'bearish', 'dump', 'crash', 'fall', 'decline', 'weak', 'fear', 'panic',
        'sell', 'fud', 'scam', 'hack', 'regulation', 'ban', 'bubble', 'burst',
        'bear market', 'red', 'loss', 'fail', 'concern', 'worry', 'risk',
        'volatility', 'uncertainty', 'doubt', 'skeptic', 'critic'
      ],
      neutral: [
        'analysis', 'technical', 'fundamental', 'market', 'trading', 'price',
        'volume', 'support', 'resistance', 'trend', 'pattern', 'indicator'
      ]
    };

    this.newsData = [];
    this.socialData = [];
    this.lastUpdate = null;
  }

  // Fetch news data (simulated - in real implementation, use news APIs)
  async fetchNewsData() {
    try {
      // Simulate news data - in production, integrate with real news APIs
      const mockNews = [
        {
          title: "Bitcoin Reaches New All-Time High Amid Institutional Adoption",
          content: "Major corporations continue to add Bitcoin to their balance sheets, driving bullish sentiment in the market.",
          source: "coindesk.com",
          timestamp: new Date().toISOString(),
          sentiment: 0.8
        },
        {
          title: "Ethereum 2.0 Upgrade Shows Promising Results",
          content: "The latest Ethereum upgrade demonstrates improved scalability and reduced transaction costs.",
          source: "cointelegraph.com",
          timestamp: new Date().toISOString(),
          sentiment: 0.7
        },
        {
          title: "Regulatory Concerns Continue to Impact Crypto Markets",
          content: "Government officials express concerns about cryptocurrency regulation and its impact on traditional finance.",
          source: "theblock.co",
          timestamp: new Date().toISOString(),
          sentiment: -0.3
        }
      ];

      this.newsData = mockNews;
      return mockNews;
    } catch (error) {
      console.error('Error fetching news data:', error);
      return [];
    }
  }

  // Fetch social media data (simulated)
  async fetchSocialData() {
    try {
      // Simulate social media data - in production, integrate with social APIs
      const mockSocial = [
        {
          content: "Just bought more BTC! Diamond hands! 🚀",
          source: "twitter.com",
          timestamp: new Date().toISOString(),
          likes: 150,
          retweets: 45
        },
        {
          content: "Ethereum looking strong today, expecting a breakout soon",
          source: "reddit.com/r/ethereum",
          timestamp: new Date().toISOString(),
          upvotes: 89
        },
        {
          content: "Market seems uncertain, might be time to take some profits",
          source: "reddit.com/r/cryptocurrency",
          timestamp: new Date().toISOString(),
          upvotes: 23
        }
      ];

      this.socialData = mockSocial;
      return mockSocial;
    } catch (error) {
      console.error('Error fetching social data:', error);
      return [];
    }
  }

  // Analyze sentiment of text
  analyzeTextSentiment(text) {
    const words = text.toLowerCase().split(/\s+/);
    let positiveScore = 0;
    let negativeScore = 0;
    let neutralScore = 0;

    words.forEach(word => {
      if (this.sentimentKeywords.positive.includes(word)) {
        positiveScore += 1;
      } else if (this.sentimentKeywords.negative.includes(word)) {
        negativeScore += 1;
      } else if (this.sentimentKeywords.neutral.includes(word)) {
        neutralScore += 1;
      }
    });

    const totalWords = words.length;
    const totalSentimentWords = positiveScore + negativeScore + neutralScore;

    if (totalSentimentWords === 0) {
      return { score: 0, confidence: 0, breakdown: { positive: 0, negative: 0, neutral: 1 } };
    }

    const normalizedPositive = positiveScore / totalSentimentWords;
    const normalizedNegative = negativeScore / totalSentimentWords;
    const normalizedNeutral = neutralScore / totalSentimentWords;

    const score = (normalizedPositive - normalizedNegative) * 100; // -100 to +100
    const confidence = totalSentimentWords / totalWords; // 0 to 1

    return {
      score: Math.max(-100, Math.min(100, score)),
      confidence: Math.max(0, Math.min(1, confidence)),
      breakdown: {
        positive: normalizedPositive,
        negative: normalizedNegative,
        neutral: normalizedNeutral
      }
    };
  }

  // Analyze news sentiment
  analyzeNewsSentiment(newsData = null) {
    const data = newsData || this.newsData;
    if (!data || data.length === 0) {
      return { score: 0, confidence: 0, count: 0 };
    }

    let totalScore = 0;
    let totalConfidence = 0;
    let validArticles = 0;

    data.forEach(article => {
      const text = `${article.title} ${article.content}`;
      const sentiment = this.analyzeTextSentiment(text);
      
      if (sentiment.confidence > 0.1) { // Only count articles with meaningful sentiment
        totalScore += sentiment.score;
        totalConfidence += sentiment.confidence;
        validArticles++;
      }
    });

    if (validArticles === 0) {
      return { score: 0, confidence: 0, count: 0 };
    }

    return {
      score: totalScore / validArticles,
      confidence: totalConfidence / validArticles,
      count: validArticles
    };
  }

  // Analyze social media sentiment
  analyzeSocialSentiment(socialData = null) {
    const data = socialData || this.socialData;
    if (!data || data.length === 0) {
      return { score: 0, confidence: 0, count: 0 };
    }

    let totalScore = 0;
    let totalConfidence = 0;
    let validPosts = 0;

    data.forEach(post => {
      const sentiment = this.analyzeTextSentiment(post.content);
      
      if (sentiment.confidence > 0.1) {
        // Weight by engagement (likes, upvotes, etc.)
        const engagement = (post.likes || 0) + (post.upvotes || 0) + (post.retweets || 0);
        const weight = Math.log(engagement + 1) + 1; // Logarithmic weighting
        
        totalScore += sentiment.score * weight;
        totalConfidence += sentiment.confidence * weight;
        validPosts += weight;
      }
    });

    if (validPosts === 0) {
      return { score: 0, confidence: 0, count: 0 };
    }

    return {
      score: totalScore / validPosts,
      confidence: totalConfidence / validPosts,
      count: data.length
    };
  }

  // Get overall market sentiment
  async getMarketSentiment() {
    try {
      // Fetch latest data
      await this.fetchNewsData();
      await this.fetchSocialData();

      const newsSentiment = this.analyzeNewsSentiment();
      const socialSentiment = this.analyzeSocialSentiment();

      // Weighted average (news 60%, social 40%)
      const overallScore = (newsSentiment.score * 0.6) + (socialSentiment.score * 0.4);
      const overallConfidence = (newsSentiment.confidence * 0.6) + (socialSentiment.confidence * 0.4);

      const sentiment = {
        overall: {
          score: Math.max(-100, Math.min(100, overallScore)),
          confidence: Math.max(0, Math.min(1, overallConfidence)),
          level: this.getSentimentLevel(overallScore)
        },
        news: {
          score: newsSentiment.score,
          confidence: newsSentiment.confidence,
          count: newsSentiment.count
        },
        social: {
          score: socialSentiment.score,
          confidence: socialSentiment.confidence,
          count: socialSentiment.count
        },
        timestamp: new Date().toISOString(),
        recommendations: this.generateSentimentRecommendations(overallScore, overallConfidence)
      };

      this.lastUpdate = new Date();
      return sentiment;
    } catch (error) {
      console.error('Error analyzing market sentiment:', error);
      return {
        overall: { score: 0, confidence: 0, level: 'NEUTRAL' },
        news: { score: 0, confidence: 0, count: 0 },
        social: { score: 0, confidence: 0, count: 0 },
        timestamp: new Date().toISOString(),
        recommendations: ['Unable to analyze sentiment at this time']
      };
    }
  }

  getSentimentLevel(score) {
    if (score > 30) return 'VERY_BULLISH';
    if (score > 10) return 'BULLISH';
    if (score > -10) return 'NEUTRAL';
    if (score > -30) return 'BEARISH';
    return 'VERY_BEARISH';
  }

  generateSentimentRecommendations(score, confidence) {
    const recommendations = [];

    if (confidence < 0.3) {
      recommendations.push('Low confidence in sentiment analysis - consider additional data sources');
    }

    if (score > 20 && confidence > 0.5) {
      recommendations.push('Strong positive sentiment - consider bullish positions');
    } else if (score < -20 && confidence > 0.5) {
      recommendations.push('Strong negative sentiment - consider bearish positions or wait for better entry');
    } else if (Math.abs(score) < 10) {
      recommendations.push('Neutral sentiment - market indecision, consider range trading');
    }

    if (score > 50) {
      recommendations.push('Extremely bullish sentiment - watch for potential reversal signals');
    } else if (score < -50) {
      recommendations.push('Extremely bearish sentiment - watch for potential bounce signals');
    }

    return recommendations;
  }

  // Get sentiment-based trading signals
  getSentimentSignals(sentiment) {
    const signals = [];

    if (sentiment.overall.confidence > 0.5) {
      if (sentiment.overall.score > 20) {
        signals.push({
          type: 'SENTIMENT_BULLISH',
          action: 'BUY',
          confidence: sentiment.overall.confidence,
          reason: `Strong positive sentiment (${sentiment.overall.score.toFixed(1)})`,
          strength: Math.min(sentiment.overall.score / 50, 1)
        });
      } else if (sentiment.overall.score < -20) {
        signals.push({
          type: 'SENTIMENT_BEARISH',
          action: 'SELL',
          confidence: sentiment.overall.confidence,
          reason: `Strong negative sentiment (${sentiment.overall.score.toFixed(1)})`,
          strength: Math.min(Math.abs(sentiment.overall.score) / 50, 1)
        });
      }
    }

    return signals;
  }

  // Monitor sentiment changes over time
  trackSentimentChanges(currentSentiment, historicalSentiment = []) {
    if (historicalSentiment.length === 0) {
      return {
        trend: 'STABLE',
        change: 0,
        volatility: 0
      };
    }

    const recentScores = historicalSentiment.slice(-10).map(s => s.overall.score);
    const currentScore = currentSentiment.overall.score;
    const averageScore = recentScores.reduce((sum, score) => sum + score, 0) / recentScores.length;
    
    const change = currentScore - averageScore;
    const volatility = this.calculateVolatility(recentScores);

    let trend = 'STABLE';
    if (change > 10) trend = 'IMPROVING';
    else if (change < -10) trend = 'DETERIORATING';

    return {
      trend,
      change: change.toFixed(1),
      volatility: volatility.toFixed(1)
    };
  }

  calculateVolatility(scores) {
    if (scores.length < 2) return 0;
    
    const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance = scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / scores.length;
    
    return Math.sqrt(variance);
  }
}

